import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type { IHttpService } from "App/Modules/Services";

import type {
    IEquipmentFunctionalityTypeSlim,
    IEquipmentType,
    IEquipmentTypeSlim,
    IResponse,
} from "App/Modules/Models";
import { EquipmentClass, EquipmentFunctionalityTypeFunctions, EquipmentTypeFunctions } from "App/Modules/Models";

import type { IEquipmentFunctionalityTypesRepository } from "App/Modules/Repositories";

import type { IEquipmentFunctionalityType } from "App/Modules/Models/Models/IEquipmentFunctionalityType";
import type { IEquipmentTypeRepository } from "./IEquipmentTypeRepository";

export default class EquipmentTypeRepository implements IEquipmentTypeRepository {
    public static $inject: string[] = ["mmHttpService", "$translate", "irEquipmentFunctionalityTypesRepository"];

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly equipmentFunctionalityTypesRepository: IEquipmentFunctionalityTypesRepository,
    ) {}

    public getEquipmentTypes(): Observable<IResponse<IEquipmentTypeSlim[]>> {
        return this.httpService.get<IEquipmentTypeSlim[]>("equipmentTypes").pipe(
            map((response: IResponse<IEquipmentTypeSlim[]>) => {
                response.data = response.data.map((eqTypeSlim: IEquipmentTypeSlim) =>
                    EquipmentTypeFunctions.enhanceEquipmentType(eqTypeSlim),
                );
                return response;
            }),
        );
    }

    public getEquipmentType(equipmentTypeId: string): Observable<IResponse<IEquipmentType>> {
        return this.httpService
            .get<IEquipmentType>(`equipmentTypes/${equipmentTypeId}`)
            .pipe(map((response: IResponse<IEquipmentType>) => this.enhanceEquipmentTypeResponse(response)));
    }

    public getEquipmentFunctionalityTypes(
        equipmentTypeId: string,
    ): Observable<IResponse<IEquipmentFunctionalityType[]>> {
        return this.httpService
            .get<IEquipmentFunctionalityType[]>(`equipmentTypes/${equipmentTypeId}/functionalities`)
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityType[]>) => {
                    response.data = response.data.map((type) =>
                        EquipmentFunctionalityTypeFunctions.enhanceFunctionalityType(type),
                    );

                    return response;
                }),
            );
    }

    public addEquipmentType(equipmentType: IEquipmentTypeSlim): Observable<IResponse<IEquipmentTypeSlim>> {
        return this.httpService
            .post<IEquipmentTypeSlim>("equipmentTypes", equipmentType)
            .pipe(map((response: IResponse<IEquipmentTypeSlim>) => this.enhanceEquipmentTypeSlimResponse(response)));
    }

    public patchEquipmentType(
        equipmentTypeId: string,
        equipmentType: IEquipmentTypeSlim,
    ): Observable<IResponse<IEquipmentType>> {
        const patchObject = { ...equipmentType };
        if (patchObject.class) {
            patchObject.class = EquipmentClass[equipmentType.class] as any;
        }
        return this.httpService
            .patch<IEquipmentType>(`equipmentTypes/${equipmentTypeId}`, patchObject)
            .pipe(map((response: IResponse<IEquipmentType>) => this.enhanceEquipmentTypeResponse(response)));
    }

    public deleteEquipmentType(equipmentTypeId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`equipmentTypes/${equipmentTypeId}`);
    }

    public addFunctionality(
        equipmentTypeId: string,
        functionalityType: IEquipmentFunctionalityTypeSlim,
    ): Observable<IResponse<IEquipmentFunctionalityTypeSlim>> {
        return this.httpService
            .post<IEquipmentFunctionalityTypeSlim>(
                `equipmentTypes/${equipmentTypeId}/functionalities`,
                functionalityType,
            )
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityTypeSlim>) => {
                    response.data = EquipmentFunctionalityTypeFunctions.enhanceFunctionalityTypeSlim(response.data);
                    return response;
                }),
            );
    }

    public deleteFunctionality(equipmentTypeId: string, functionalityId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`equipmentTypes/${equipmentTypeId}/functionalities/${functionalityId}`);
    }

    public getSupportedVideoStreamProperties(
        equipmentTypeId: string,
    ): Observable<IResponse<IEquipmentFunctionalityType>> {
        return this.httpService
            .get<IEquipmentFunctionalityType>(`equipmentTypes/${equipmentTypeId}/functionalities/video`)
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityType>) => {
                    response.data = EquipmentFunctionalityTypeFunctions.enhanceFunctionalityType(response.data);
                    return response;
                }),
            );
    }

    private enhanceEquipmentTypeResponse(equipmentTypeResponse: IResponse<IEquipmentType>): IResponse<IEquipmentType> {
        if (equipmentTypeResponse.data) {
            equipmentTypeResponse.data = EquipmentTypeFunctions.enhanceEquipmentType(equipmentTypeResponse.data);
        }
        return equipmentTypeResponse;
    }

    private enhanceEquipmentTypeSlimsResponse(
        equipmentTypesResponse: IResponse<IEquipmentTypeSlim[]>,
    ): IResponse<IEquipmentTypeSlim[]> {
        if (equipmentTypesResponse.data) {
            equipmentTypesResponse.data = equipmentTypesResponse.data.map((et: IEquipmentTypeSlim) =>
                EquipmentTypeFunctions.enhanceEquipmentType(et),
            );
        }
        return equipmentTypesResponse;
    }

    private enhanceEquipmentTypeSlimResponse(
        equipmentTypeResponse: IResponse<IEquipmentTypeSlim>,
    ): IResponse<IEquipmentTypeSlim> {
        if (equipmentTypeResponse.data) {
            equipmentTypeResponse.data = EquipmentTypeFunctions.enhanceEquipmentType(equipmentTypeResponse.data);
        }
        return equipmentTypeResponse;
    }
}

module("mmRepositories").service("mmEquipmentTypeRepository", EquipmentTypeRepository);
